import { useState } from 'react'
import { Alert, Badge, Button } from '../ui'

export default function ApplianceDownloadSetupStep({ step, onBack, onNext, onDownload, onCreateEnrollment, onRefresh }) {
  const [download, setDownload] = useState(null)
  const [name, setName] = useState('')
  const [enrollment, setEnrollment] = useState(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const appliances = step?.appliances || []
  const complete = step?.status === 'complete'

  const requestDownload = async () => {
    setSaving(true)
    setError(null)
    try {
      setDownload(await onDownload())
    } catch {
      setError('We couldn\'t create a download link. Try again.')
    } finally {
      setSaving(false)
    }
  }

  const createEnrollment = async () => {
    if (!name.trim()) {
      setError('Enter a name for this appliance')
      return
    }
    setSaving(true)
    setError(null)
    try {
      setEnrollment(await onCreateEnrollment(name.trim()))
    } catch {
      setError('We couldn\'t create an enrollment code. Try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-medium text-[var(--app-text)]">Download and enroll an appliance</h2>
          <p className="mt-1 text-sm text-[var(--app-text-muted)]">One ready appliance is required. You can add more appliances later.</p>
        </div>
        {complete && <Badge color="success">Complete</Badge>}
      </div>

      {(error || step?.error) && <Alert color="danger">{error || 'Appliance status could not be refreshed. Try again.'}</Alert>}

      <div className="rounded-[var(--app-radius)] border border-[var(--app-border)] p-3 space-y-2">
        <span className="font-medium">1. Download the appliance</span>
        <p className="text-xs text-[var(--app-text-muted)]">The time-limited download link will also be sent to your email.</p>
        <Button disabled={saving} onClick={requestDownload}>Request download link</Button>
        {download?.download_url && <a className="block text-sm text-[var(--app-accent)] underline" href={download.download_url} target="_blank" rel="noreferrer">Download appliance OVA</a>}
      </div>

      <div className="rounded-[var(--app-radius)] border border-[var(--app-border)] p-3 space-y-2">
        <span className="font-medium">2. Create an enrollment code</span>
        {!enrollment ? (
          <div className="flex flex-wrap gap-2">
            <input aria-label="Appliance name" value={name} onChange={(event) => setName(event.target.value)} placeholder="Appliance - DC1…" className="min-w-64 flex-1 rounded-[var(--app-radius)] border border-[var(--app-border-strong)] bg-[var(--app-surface)] px-2.5 py-1.5" />
            <Button color="primary" disabled={saving} onClick={createEnrollment}>Create enrollment code</Button>
          </div>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2">
            <div><div className="text-xs text-[var(--app-text-muted)]">Server hostname</div><code>{enrollment.server_hostname}</code></div>
            <div><div className="text-xs text-[var(--app-text-muted)]">Enrollment code (expires in 15 minutes)</div><code className="text-base font-medium tracking-wider" translate="no">{enrollment.enrollment_code}</code></div>
          </div>
        )}
      </div>

      <div className="space-y-2">
        <span className="font-medium">3. Waiting for appliance readiness</span>
        {appliances.length === 0 ? <p className="text-sm text-[var(--app-text-muted)]">No appliance has enrolled yet.</p> : appliances.map((appliance) => (
          <div key={appliance.id} className="rounded-[var(--app-radius)] border border-[var(--app-border)] bg-[var(--app-surface-2)] p-3">
            <div className="flex items-center justify-between gap-2"><span>{appliance.name}</span><Badge color={appliance.last_heartbeat && appliance.edge_ready ? 'success' : 'warning'}>{appliance.last_heartbeat && appliance.edge_ready ? 'Ready' : 'Preparing'}</Badge></div>
            <div className="mt-1 text-xs text-[var(--app-text-muted)]">
              {!appliance.last_heartbeat
                ? 'Waiting for this appliance to connect.'
                : appliance.edge_ready
                  ? 'Connected and ready.'
                  : 'Connected. Finishing preparation.'}
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between gap-2">
        <Button onClick={onBack}>Back</Button>
        <div className="flex gap-2"><Button onClick={onRefresh}>Refresh status</Button>{complete && <Button color="primary" onClick={onNext}>Next</Button>}</div>
      </div>
    </div>
  )
}
